import {getToken, setToken, clearToken} from '../auth/token-storage.js'

const MOCK_MODE = process.env.MOCK_API === 'true'

// In browser mock mode the app talks to the MSW service worker, whose handlers
// are registered against same-origin relative paths — so the base must be empty
// for them to match. Production and `dev-server:live` use the real API origin.
// An explicit `window.__VERDASYNC_API_BASE__` always wins; jsdom (jest) has no
// service worker, so unit tests keep the absolute base they already rely on.
const MOCK_SAME_ORIGIN = MOCK_MODE
    && typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator

const API_BASE = (typeof window !== 'undefined' && window.__VERDASYNC_API_BASE__)
    || (MOCK_SAME_ORIGIN ? '' : 'https://api-verdasync.va.capital')

const REFRESH_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000

class ApiError extends Error {
    /**
     * @param {string} message
     * @param {number} status
     * @param {*} [body]
     */
    constructor(message, status, body) {
        super(message)
        this.name = 'ApiError'
        this.status = status
        this.body = body
    }
}

/**
 * Parse JWT payload without verification (verification is the server's job).
 * @param {string} token
 * @return {{exp?: number}|null}
 */
function readJwtPayload(token) {
    try {
        const payload = token.split('.')[1]
        if (!payload)
            return null
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
        return JSON.parse(decoded)
    } catch {
        return null
    }
}

let refreshInFlight = null

/**
 * Refresh the token if it's within REFRESH_THRESHOLD_MS of expiry.
 * Dedupes concurrent calls.
 * @return {Promise<void>}
 */
async function maybeRefresh() {
    const token = getToken()
    if (!token)
        return
    const payload = readJwtPayload(token)
    if (!payload?.exp)
        return
    const expiresAt = payload.exp * 1000
    if (expiresAt - Date.now() > REFRESH_THRESHOLD_MS)
        return
    if (refreshInFlight) {
        await refreshInFlight
        return
    }
    refreshInFlight = (async () => {
        try {
            const res = await fetch(`${API_BASE}/auth/refresh`, {
                method: 'POST',
                headers: {Authorization: `Bearer ${token}`}
            })
            if (!res.ok)
                return
            const body = await res.json()
            if (body?.token)
                setToken(body.token)
        } catch {
            // silent: caller will see 401 if refresh truly failed
        } finally {
            refreshInFlight = null
        }
    })()
    await refreshInFlight
}

/**
 * @typedef {Object} RequestOptions
 * @property {Object<string,*>|FormData} [body]
 * @property {Object<string,string>} [headers]
 * @property {AbortSignal} [signal]
 * @property {boolean} [skipAuth]
 */

/**
 * Issue a request to greenhouse-api. Injects bearer token, handles 401 by
 * clearing the token, and triggers a background refresh when the JWT is near
 * expiry.
 *
 * @param {string} method
 * @param {string} path
 * @param {RequestOptions} [options]
 * @return {Promise<*>}
 */
export async function request(method, path, options = {}) {
    if (!options.skipAuth)
        await maybeRefresh()

    const headers = {Accept: 'application/json', ...(options.headers || {})}
    const token = getToken()
    if (token && !options.skipAuth)
        headers.Authorization = `Bearer ${token}`

    let body
    if (options.body instanceof FormData) {
        body = options.body
    } else if (options.body != null) {
        body = JSON.stringify(options.body)
        headers['Content-Type'] = 'application/json'
    }

    const res = await fetch(`${API_BASE}${path}`, {
        method,
        headers,
        body,
        signal: options.signal,
        credentials: 'same-origin'
    })

    if (res.status === 401 && !options.skipAuth) {
        clearToken()
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth'))
            window.location.replace('/auth?invalid=1')
    }

    const contentType = res.headers.get('content-type') || ''
    const isJson = contentType.includes('application/json')
    const payload = isJson ? await res.json().catch(() => null) : await res.text().catch(() => null)

    if (!res.ok) {
        const message = (payload && (payload.error || payload.message)) || res.statusText || 'Request failed'
        throw new ApiError(message, res.status, payload)
    }
    return payload
}

export {ApiError, API_BASE}
